import React from 'react';
import { Modal, ModalLightbox, ModalDrawer } from '@blueprint/ui';

import dynamic from 'next/dynamic';
import { Modals, ModalManagerProps } from './ModalManager.types';

import {
    trackAdditionalLinksModalClose,
    trackEnergyPerfCertModalClose,
    trackFloorPlansModalClose,
    trackGuidePriceModalClose,
    trackListingHistoryModalClose,
    trackMapModalClose,
    trackMarketStatsModalClose,
    trackSharingModalClose,
    trackStreetViewModalClose,
    trackSmartViewingsModalClose,
    trackTenancyInfoModalClose,
    trackVirtualToursModalClose
} from './ModalManager.tracking';

export const MODALS: Modals = {
    map: {
        title: 'Map view',
        trackClose: trackMapModalClose,
        content: dynamic(
            () =>
                import(
                    'listing-details/components/ListingInteractiveMap/ListingInteractiveMap'
                ),
            { ssr: false }
        ),
        type: ModalLightbox
    },
    streetview: {
        title: 'Street view',
        trackClose: trackStreetViewModalClose,
        content: dynamic(
            () =>
                import(
                    'listing-details/components/ListingInteractiveMap/ListingInteractiveMap'
                ),
            { ssr: false }
        ),
        type: ModalLightbox,
        props: { showStreetView: true }
    },
    tours: {
        title: 'Virtual tours',
        trackClose: trackVirtualToursModalClose,
        content: dynamic(
            () =>
                import('listing-details/components/VirtualTours/VirtualTours'),
            { ssr: false }
        ),
        type: ModalLightbox
    },
    floorplans: {
        title: 'Floor plans',
        trackClose: trackFloorPlansModalClose,
        content: dynamic(
            () => import('listing-details/components/FloorPlans/FloorPlans'),
            { ssr: false }
        ),
        type: ModalLightbox
    },
    energy: {
        title: 'Energy Performance Certificate',
        trackClose: trackEnergyPerfCertModalClose,
        content: dynamic(
            () =>
                import(
                    'listing-details/components/EnergyPerfCert/EnergyPerfCert'
                ),
            { ssr: false }
        ),
        type: ModalLightbox
    },
    sharing: {
        trackClose: trackSharingModalClose,
        content: dynamic(
            () =>
                import(
                    'listing-details/components/ListingSharing/ListingSharing'
                ),
            { ssr: false }
        ),
        type: ModalDrawer
    },
    tenancy_info: {
        trackClose: trackTenancyInfoModalClose,
        content: dynamic(
            () => import('listing-details/components/TenancyInfo/TenancyInfo'),
            { ssr: false }
        ),
        type: Modal
    },
    guide_price: {
        trackClose: trackGuidePriceModalClose,
        content: dynamic(
            () => import('listing-details/components/GuidePrice/GuidePrice'),
            { ssr: false }
        ),
        type: Modal
    },
    additional_links: {
        trackClose: trackAdditionalLinksModalClose,
        content: dynamic(
            () =>
                import(
                    'listing-details/components/AdditionalLinks/AdditionalLinks'
                ),
            { ssr: false }
        ),
        type: ModalDrawer
    },
    listing_history: {
        trackClose: trackListingHistoryModalClose,
        content: dynamic(
            () =>
                import(
                    'listing-details/components/ListingHistory/ListingHistory'
                ),
            { ssr: false }
        ),
        type: ModalDrawer
    },
    smartViewing: {
        title: 'Smart Viewing',
        trackClose: trackSmartViewingsModalClose,
        content: dynamic(
            () =>
                import(
                    'listing-details/components/SmartViewings/SmartViewings'
                ),
            { ssr: false }
        ),
        type: ModalLightbox
    },
    market_stats: {
        trackClose: trackMarketStatsModalClose,
        content: dynamic(
            () => import('listing-details/components/MarketStats/MarketStats'),
            { ssr: false }
        ),
        type: ModalDrawer
    }
};

const ModalManager = ({
    data,
    listingId,
    listingSection,
    activeModal,
    isModalShowing,
    closeModal
}: ModalManagerProps) => {
    const Modal = MODALS[activeModal].type;
    const Content = MODALS[activeModal].content;
    const props = MODALS[activeModal].props;

    if (!data) {
        return null;
    }
    return (
        <Modal
            title={MODALS[activeModal].title || ''}
            isShowing={isModalShowing}
            close={closeModal}
        >
            <Content
                data={data}
                listingId={listingId}
                listingSection={listingSection}
                {...props}
            />
        </Modal>
    );
};

export default ModalManager;
